import {Component, EventEmitter, Input, Optional, Output} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'hmt-relative-date-selector',
  templateUrl: './relative-date-selector.component.html',
  styleUrls: ['./relative-date-selector.component.scss'],
  standalone: false
})
export class RelativeDateSelectorComponent {
  @Input() baseDate: Date = new Date();
  @Input() requireFutureDateTime = false;
  pastDateTimeError = false;
  private _options: number[] = [1, 3, 7];
  @Input() set options(value: number[]) {
    if (Array.isArray(value) && value.length) {
      this._options = value.filter((v) => v !== 14);
    } else {
      this._options = [1, 3, 7];
    }
  }
  get options(): number[] {
    return this._options;
  }
  @Input() selectedDate: Date | null = null;

  get minDate(): Date {
    // Set minDate to today (start of day) to disable past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  get today(): Date {
    // Get today at start of day for comparisons
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  get availableOptions(): number[] {
    // Filter out options that would result in past dates
    const today = this.today;
    
    return this._options.filter(days => {
      const optionDate = this.getOptionDate(days);
      const dateToCheck = new Date(optionDate);
      dateToCheck.setHours(0, 0, 0, 0);
      return dateToCheck >= today;
    });
  }

  @Output() dateChange = new EventEmitter<Date>();
  
  showCustomPicker = false;
  selectedOption: number | 'custom' = 1;

  constructor(@Optional() private dialogRef?: MatDialogRef<RelativeDateSelectorComponent>) {}

  getOptionDate(days: number): Date {
    const d = new Date(this.baseDate);
    d.setDate(d.getDate() + days);
    return d;
  }

  getOptionDateWithTime(days: number): Date {
    const date = this.getOptionDate(days);
    // Set consistent time (default to 9:00 AM if baseDate doesn't have a specific time set)
    const baseDateHours = this.baseDate.getHours();
    const baseDateMinutes = this.baseDate.getMinutes();
    
    // If baseDate has default time (midnight or close to it), set to 9:00 AM
    // Otherwise preserve the time from baseDate
    if (baseDateHours === 0 && baseDateMinutes === 0) {
      date.setHours(9, 0, 0, 0);
    } else {
      date.setHours(baseDateHours, baseDateMinutes, 0, 0);
    }
    return date;
  }

  formatDayCount(days: number): string {
    return `${days} ${days === 1 ? 'day' : 'days'}`;
  }

  formatMonthDate(date: Date): string {
    const formatter = new Intl.DateTimeFormat('en-GB', {day: '2-digit', month: 'long'});
    const parts = formatter.formatToParts(date);
    const day = parts.find(p => p.type === 'day')?.value ?? '';
    const month = parts.find(p => p.type === 'month')?.value ?? '';
    return `${day} ${month}`;
  }

  formatDateWithTime(date: Date): string {
    // Format to match the summary format: 'dd MMMM yyyy : hh.mm a'
    const day = date.getDate().toString().padStart(2, '0');
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    const hours24 = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const period = hours24 >= 12 ? 'pm' : 'am';
    let hour12 = hours24 % 12;
    hour12 = hour12 === 0 ? 12 : hour12;
    const hour = hour12.toString().padStart(2, '0');
    
    return `${day} ${month} ${year} : ${hour}.${minutes} ${period}`;
  }

  selectRelativeDays(days: number): void {
    const date = this.getOptionDateWithTime(days);
    const today = this.today;
    const dateToCheck = new Date(date);
    dateToCheck.setHours(0, 0, 0, 0);
    
    if (dateToCheck < today) {
      return;
    }
    
    this.pastDateTimeError = false;
    this.showCustomPicker = false;
    this.selectedDate = date;
    this.dateChange.emit(date);
    this.selectedOption = days;
  }

  openCustomPicker(): void {
    this.selectedOption = 'custom';
    this.showCustomPicker = true;
  }

  onCustomDateSelected(date: Date | null): void {
    if (!date) { return; }
    
    const today = this.today;
    const dateToCheck = new Date(date);
    dateToCheck.setHours(0, 0, 0, 0);
    
    if (dateToCheck < today) {
      return;
    }
    
    this.pastDateTimeError = false;
    this.selectedDate = date;
    this.dateChange.emit(date);
  }

  onApply(): void {
    if (!this.selectedDate) { return; }

    if (this.requireFutureDateTime && this.selectedDate.getTime() <= Date.now()) {
      this.pastDateTimeError = true;
      return;
    }

    this.pastDateTimeError = false;
    if (this.dialogRef) {
      this.dialogRef.close(this.selectedDate);
    }
  }

  onClose(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }
}


